// src/components/help/HelpPage.jsx
import React, { useState } from 'react';
import {
    Container, Typography, Box, Paper, Grid,
    Accordion, AccordionSummary, AccordionDetails,
    Tabs, Tab, Card, CardContent, CardMedia,
    Divider, Link, Breadcrumbs, Button,
    List, ListItem, ListItemIcon, ListItemText
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    QuestionAnswer as FAQIcon,
    Info as InfoIcon,
    ContactSupport as ContactIcon,
    Assignment as AssignmentIcon,
    Security as SecurityIcon,
    Business as BusinessIcon,
    HealthAndSafety as HealthIcon,
    Inventory as InventoryIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

const HelpPage = () => {
    const [tabValue, setTabValue] = useState(0);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs sx={{ mb: 2 }}>
                <Link component={RouterLink} to="/dashboard" underline="hover" color="inherit">
                    Dashboard
                </Link>
                <Typography color="text.primary">Help</Typography>
            </Breadcrumbs>

            <Typography variant="h4" gutterBottom>
                Help Center
            </Typography>

            <Typography variant="body1" color="text.secondary" paragraph>
                Welcome to the RiskAS Help Center. Here you'll find guides, FAQs, and resources to help you make the most of our risk assessment system.
            </Typography>

            {/* Tab Navigation */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    aria-label="help center tabs"
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    <Tab label="Getting Started" icon={<InfoIcon />} iconPosition="start" />
                    <Tab label="FAQs" icon={<FAQIcon />} iconPosition="start" />
                    <Tab label="Risk Domains" icon={<SecurityIcon />} iconPosition="start" />
                    <Tab label="Contact Support" icon={<ContactIcon />} iconPosition="start" />
                </Tabs>
            </Box>

            {/* Getting Started Tab */}
            {tabValue === 0 && (
                <Box>
                    <Typography variant="h5" sx={{ mb: 3 }}>Getting Started with RiskAS</Typography>

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Card variant="outlined">
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        System Overview
                                    </Typography>
                                    <Typography variant="body2" paragraph>
                                        RiskAS is a comprehensive risk assessment system designed to help organizations identify,
                                        analyze, and mitigate risks across multiple domains.
                                    </Typography>
                                    <Typography variant="body2">
                                        Our AI-powered platform provides insights and recommendations based on your
                                        specific risk factors, helping you make informed decisions to protect your
                                        organization.
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Card variant="outlined">
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Key Features
                                    </Typography>
                                    <List dense>
                                        <ListItem>
                                            <ListItemIcon>
                                                <AssignmentIcon color="primary" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary="Comprehensive Risk Assessments"
                                                secondary="Across financial, cybersecurity, health, and operational domains"
                                            />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemIcon>
                                                <SecurityIcon color="primary" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary="AI-Powered Analysis"
                                                secondary="Advanced insights powered by Claude AI"
                                            />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemIcon>
                                                <InfoIcon color="primary" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary="Detailed Reports"
                                                secondary="Actionable recommendations and mitigation strategies"
                                            />
                                        </ListItem>
                                    </List>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12}>
                            <Paper variant="outlined" sx={{ p: 3, mt: 2 }}>
                                <Typography variant="h6" gutterBottom>
                                    Quick Start Guide
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={4}>
                                        <Box sx={{ textAlign: 'center', p: 2 }}>
                                            <Box sx={{
                                                fontSize: '24px', fontWeight: 'bold', mb: 1,
                                                height: 40, width: 40,
                                                borderRadius: '50%',
                                                bgcolor: 'primary.main',
                                                color: 'white',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mx: 'auto'
                                            }}>
                                                1
                                            </Box>
                                            <Typography variant="h6" gutterBottom>Create Assessment</Typography>
                                            <Typography variant="body2">
                                                Select "New Assessment" from the sidebar and choose the appropriate domain for your risk evaluation.
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Box sx={{ textAlign: 'center', p: 2 }}>
                                            <Box sx={{
                                                fontSize: '24px', fontWeight: 'bold', mb: 1,
                                                height: 40, width: 40,
                                                borderRadius: '50%',
                                                bgcolor: 'primary.main',
                                                color: 'white',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mx: 'auto'
                                            }}>
                                                2
                                            </Box>
                                            <Typography variant="h6" gutterBottom>Input Risk Factors</Typography>
                                            <Typography variant="body2">
                                                Fill in the required information about potential risks specific to your selected domain.
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Box sx={{ textAlign: 'center', p: 2 }}>
                                            <Box sx={{
                                                fontSize: '24px', fontWeight: 'bold', mb: 1,
                                                height: 40, width: 40,
                                                borderRadius: '50%',
                                                bgcolor: 'primary.main',
                                                color: 'white',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mx: 'auto'
                                            }}>
                                                3
                                            </Box>
                                            <Typography variant="h6" gutterBottom>Review Results</Typography>
                                            <Typography variant="body2">
                                                Analyze the AI-generated risk assessment report and implement recommended mitigation strategies.
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                                <Box sx={{ mt: 3, textAlign: 'center' }}>
                                    <Button
                                        variant="contained"
                                        component={RouterLink}
                                        to="/new-assessment"
                                    >
                                        Start Your First Assessment
                                    </Button>
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>
            )}

            {/* FAQs Tab */}
            {tabValue === 1 && (
                <Box>
                    <Typography variant="h5" sx={{ mb: 3 }}>Frequently Asked Questions</Typography>

                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle1">What types of risk can I assess with RiskAS?</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography variant="body1">
                                RiskAS supports four main risk domains:
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemText
                                        primary="Financial Risk"
                                        secondary="Including debt, credit, liquidity, and investment risks"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemText
                                        primary="Cybersecurity Risk"
                                        secondary="Covering vulnerabilities, threats, and security control effectiveness"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemText
                                        primary="Health & Safety Risk"
                                        secondary="Focused on workplace safety, hazard identification, and compliance"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemText
                                        primary="Operational Risk"
                                        secondary="Including supply chain, process, and resource dependencies"
                                    />
                                </ListItem>
                            </List>
                        </AccordionDetails>
                    </Accordion>

                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle1">How does the AI-powered analysis work?</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography>
                                Our system uses Anthropic's Claude AI to analyze your risk data. After you input risk factors,
                                our AI examines patterns, trends, and potential impacts to generate a comprehensive
                                risk assessment. The AI provides insights across multiple dimensions, including:
                            </Typography>
                            <ul>
                                <li>Overall risk level determination</li>
                                <li>Key vulnerability identification</li>
                                <li>Detailed risk analysis based on industry best practices</li>
                                <li>Actionable recommendations for risk mitigation</li>
                            </ul>
                            <Typography>
                                The AI-driven approach ensures you receive objective, data-driven assessments
                                that might identify risks human analysts could miss.
                            </Typography>
                        </AccordionDetails>
                    </Accordion>

                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle1">How should I interpret my risk score?</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography paragraph>
                                Risk scores in RiskAS range from 1.0 (lowest risk) to 10.0 (highest risk).
                                The scores are interpreted as follows:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                                <Box sx={{ p: 1, bgcolor: '#388e3c20', borderRadius: 1, width: 200 }}>
                                    <Typography variant="subtitle2" sx={{ color: '#388e3c', fontWeight: 'bold' }}>
                                        Low Risk (1.0 - 3.9)
                                    </Typography>
                                    <Typography variant="body2">
                                        Minimal concerns, regular monitoring recommended
                                    </Typography>
                                </Box>
                                <Box sx={{ p: 1, bgcolor: '#f57c0020', borderRadius: 1, width: 200 }}>
                                    <Typography variant="subtitle2" sx={{ color: '#f57c00', fontWeight: 'bold' }}>
                                        Medium Risk (4.0 - 6.9)
                                    </Typography>
                                    <Typography variant="body2">
                                        Moderate concerns, specific actions needed
                                    </Typography>
                                </Box>
                                <Box sx={{ p: 1, bgcolor: '#d32f2f20', borderRadius: 1, width: 200 }}>
                                    <Typography variant="subtitle2" sx={{ color: '#d32f2f', fontWeight: 'bold' }}>
                                        High Risk (7.0 - 10.0)
                                    </Typography>
                                    <Typography variant="body2">
                                        Significant concerns, immediate action required
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography>
                                Each risk score is accompanied by specific findings and recommendations
                                to help you address the identified risks effectively.
                            </Typography>
                        </AccordionDetails>
                    </Accordion>

                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle1">How often should I conduct risk assessments?</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography>
                                The frequency of risk assessments depends on your specific industry, regulatory requirements,
                                and organizational needs. Generally, we recommend:
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemText
                                        primary="Quarterly assessments for high-volatility areas"
                                        secondary="Such as cybersecurity or rapidly changing financial environments"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemText
                                        primary="Semi-annual assessments for moderate risk areas"
                                        secondary="Including most operational and health & safety concerns"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemText
                                        primary="Annual comprehensive assessments"
                                        secondary="A full review of all risk domains"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemText
                                        primary="Ad-hoc assessments following significant changes"
                                        secondary="Such as after mergers, new regulations, or major incidents"
                                    />
                                </ListItem>
                            </List>
                        </AccordionDetails>
                    </Accordion>

                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle1">Can I export my risk assessment reports?</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography>
                                Yes, all risk assessment reports can be exported in multiple formats:
                            </Typography>
                            <ul>
                                <li>PDF reports for sharing with stakeholders</li>
                                <li>CSV data export for further analysis</li>
                                <li>JSON format for integration with other systems</li>
                            </ul>
                            <Typography>
                                To export a report, navigate to the assessment details page and click the
                                "Download" or "Export" button in the top-right corner.
                            </Typography>
                        </AccordionDetails>
                    </Accordion>
                </Box>
            )}

            {/* Risk Domains Tab */}
            {tabValue === 2 && (
                <Box>
                    <Typography variant="h5" sx={{ mb: 3 }}>Risk Domains</Typography>

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', bgcolor: 'primary.light' }}>
                                    <BusinessIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                                    <Typography variant="h6" color="primary.main">Financial Risk</Typography>
                                </Box>
                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Typography variant="body2" paragraph>
                                        Financial risk assessment focuses on identifying and managing risks related to financial
                                        stability, liquidity, credit, and investment decisions.
                                    </Typography>
                                    <Typography variant="subtitle2" gutterBottom>Key Risk Factors:</Typography>
                                    <List dense>
                                        <ListItem>
                                            <ListItemText primary="Debt-to-Income Ratio" />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemText primary="Credit History" />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemText primary="Liquidity Position" />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemText primary="Market Exposure" />
                                        </ListItem>
                                    </List>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', bgcolor: 'primary.light' }}>
                                    <SecurityIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                                    <Typography variant="h6" color="primary.main">Cybersecurity Risk</Typography>
                                </Box>
                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Typography variant="body2" paragraph>
                                        Cybersecurity risk assessment evaluates potential threats, vulnerabilities, and impacts
                                        to information systems and digital assets.
                                    </Typography>
                                    <Typography variant="subtitle2" gutterBottom>Key Risk Factors:</Typography>
                                    <List dense>
                                        <ListItem>
                                            <ListItemText primary="Vulnerability Count & Severity" />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemText primary="Security Control Maturity" />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemText primary="Threat Landscape" />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemText primary="Data Sensitivity" />
                                        </ListItem>
                                    </List>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', bgcolor: 'primary.light' }}>
                                    <HealthIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                                    <Typography variant="h6" color="primary.main">Health & Safety Risk</Typography>
                                </Box>
                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Typography variant="body2" paragraph>
                                        Health and safety risk assessment identifies hazards in the workplace and evaluates
                                        controls to prevent injuries, illnesses, and ensure regulatory compliance.
                                    </Typography>
                                    <Typography variant="subtitle2" gutterBottom>Key Risk Factors:</Typography>
                                    <List dense>
                                        <ListItem>
                                            <ListItemText primary="Hazard Identification" />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemText primary="Occupancy & Facility Type" />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemText primary="Historical Incidents" />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemText primary="Control Effectiveness" />
                                        </ListItem>
                                    </List>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', bgcolor: 'primary.light' }}>
                                    <InventoryIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                                    <Typography variant="h6" color="primary.main">Operational Risk</Typography>
                                </Box>
                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Typography variant="body2" paragraph>
                                        Operational risk assessment examines process failures, system breakdowns, human
                                        errors, and external events that could disrupt business operations.
                                    </Typography>
                                    <Typography variant="subtitle2" gutterBottom>Key Risk Factors:</Typography>
                                    <List dense>
                                        <ListItem>
                                            <ListItemText primary="Process Dependencies" />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemText primary="Resource Availability" />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemText primary="Supply Chain Resilience" />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemText primary="Business Continuity Planning" />
                                        </ListItem>
                                    </List>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>
            )}

            {/* Contact Support Tab */}
            {tabValue === 3 && (
                <Box>
                    <Typography variant="h5" sx={{ mb: 3 }}>Contact Support</Typography>

                    <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Need Additional Help?
                        </Typography>
                        <Typography paragraph>
                            Our support team is available to assist you with any questions or issues you may encounter
                            while using the RiskAS platform.
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={4}>
                                <Box sx={{ textAlign: 'center', p: 2 }}>
                                    <ContactIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                                    <Typography variant="h6" gutterBottom>Email Support</Typography>
                                    <Typography variant="body2">
                                        For general inquiries and non-urgent issues
                                    </Typography>
                                    <Link href="mailto:wakinniranye@yahoo.com" sx={{ display: 'block', mt: 1 }}>
                                        wakinniranye@yahoo.com
                                    </Link>
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <Box sx={{ textAlign: 'center', p: 2 }}>
                                    <ContactIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                                    <Typography variant="h6" gutterBottom>Phone Support</Typography>
                                    <Typography variant="body2">
                                        For urgent assistance during business hours
                                    </Typography>
                                    <Link href="tel:+447448850674" sx={{ display: 'block', mt: 1 }}>
                                        +447448850674
                                    </Link>
                                </Box>
                            </Grid>


                        </Grid>
                    </Paper>


                </Box>
            )}
        </Container>
    );
};

export default HelpPage;